import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { Roles } from '../types';

interface Props {
  data: Roles;
}

const ROLE_LABELS: Record<string, string> = {
  admin: 'Admin',
  course_manager: 'Course Manager',
  content_manager: 'Content Manager',
  course_viewer: 'Course Viewer',
  editor: 'Editor',
  learner: 'Learner',
};

const ROLE_COLORS: Record<string, string> = {
  admin: '#6366f1',
  course_manager: '#22c55e',
  content_manager: '#f59e0b',
  course_viewer: '#3b82f6',
  editor: '#8b5cf6',
  learner: '#06b6d4',
};

export const RolesChart: React.FC<Props> = ({ data }) => {
  const chartData = Object.entries(data)
    .map(([key, value]) => ({
      name: ROLE_LABELS[key] || key,
      value: value as number,
      key,
    }))
    .filter((item) => item.value > 0);

  const total = chartData.reduce((sum, item) => sum + item.value, 0);

  if (total === 0) {
    return (
      <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, padding: 24 }}>
        <span style={{ fontSize: 13, fontWeight: 500, color: '#6b7280' }}>User Roles</span>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 160 }}>
          <p style={{ fontSize: 13, color: '#d1d5db' }}>No users found</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, padding: 24 }}>
      <span style={{ fontSize: 13, fontWeight: 500, color: '#6b7280', display: 'block', marginBottom: 24 }}>
        User Roles
      </span>

      {/* Chart */}
      <div style={{ width: '100%', height: 160, position: 'relative', marginBottom: 20 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              innerRadius={42}
              outerRadius={72}
              dataKey="value"
              paddingAngle={2}
              strokeWidth={0}
            >
              {chartData.map((entry) => (
                <Cell key={entry.key} fill={ROLE_COLORS[entry.key] || '#d1d5db'} />
              ))}
            </Pie>
            <Tooltip
              formatter={(value: number, name: string) => [
                `${value} (${((value / total) * 100).toFixed(1)}%)`,
                name,
              ]}
              contentStyle={{
                borderRadius: 8,
                border: '1px solid #e5e7eb',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                fontSize: 12,
                padding: '8px 12px',
              }}
            />
          </PieChart>
        </ResponsiveContainer>
        {/* Center label */}
        <div style={{
          position: 'absolute', inset: 0,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          pointerEvents: 'none',
        }}>
          <span style={{ fontSize: 18, fontWeight: 600, color: '#111827', fontVariantNumeric: 'tabular-nums' }}>{total}</span>
          <span style={{ fontSize: 11, color: '#6B7280' }}>total</span>
        </div>
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {chartData.map((item) => {
          const pct = ((item.value / total) * 100).toFixed(0);
          return (
            <div key={item.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div
                  style={{
                    width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                    backgroundColor: ROLE_COLORS[item.key] || '#d1d5db',
                  }}
                />
                <span style={{ fontSize: 13, color: '#374151' }}>{item.name}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ fontSize: 12, color: '#6B7280', fontVariantNumeric: 'tabular-nums' }}>{pct}%</span>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#111827', fontVariantNumeric: 'tabular-nums', width: 24, textAlign: 'right' as const }}>{item.value}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
